import CatExposureUploadDialog from "./CatExposureUploadDialog";
import CatExposuresGrid from "./CatExposuresGrid";
import { generateTemplate } from "./catExposureUploadUtils";
import { getExposures, PRIOR_PREFIX } from "./manualCatExposuresUtils";
import {
  FileUpload as UploadIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { Box, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import * as ids from "common/ids";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import Metrics from "components/common/Metrics";
import Visible from "components/common/Visible";
import { usePricing } from "components/customPricing/dnfModel1/pricingHooks";
import { CAT_EXPOSURES_UPLOAD_ENABLED } from "flags";
import { useSnackbar } from "notistack";
import { useEffect, useCallback, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as analyticsStore from "store/analytics";
import * as catExposuresStore from "store/catExposures";
import * as stateSelectors from "store/selectors/stateSelectors";

const EMPTY_OBJ = Object.freeze({});

const extractPerilMetrics = (exposures) => {
  const perilMap = exposures.reduce((acc, exposure) => {
    const peril = exposure._SUBLIMIT_ZONE?.split?.(".")?.[0] ?? "Unclassified";

    acc[peril] =
      acc[peril] == null
        ? { peril, totalValue: exposure._VALUE ?? 0 }
        : {
            ...acc[peril],
            totalValue: acc[peril].totalValue + (exposure._VALUE ?? 0),
          };

    return acc;
  }, {});

  const metrics = Object.values(perilMap).map(({ peril, totalValue }) => ({
    label: peril,
    value: totalValue,
    prefix: "$",
  }));

  return metrics.length === 0 ? [{ label: "No Data", value: "-" }] : metrics;
};

const ManualCatExposures = ({ catModel, useMultiCatModelMode }) => {
  const theme = useTheme();
  const [nextId, setNextId] = useState(ids.getUniqueKey());
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const dispatch = useDispatch();

  const catExposures = useSelector(catExposuresStore.selectCatExposuresInput);
  const allManualExposures = catExposures?.manualExposures;
  const manualExposures = useMemo(
    () =>
      useMultiCatModelMode == null
        ? []
        : useMultiCatModelMode
        ? allManualExposures?.[catModel] ?? []
        : allManualExposures ?? [],
    [allManualExposures, catModel, useMultiCatModelMode]
  );

  const allManualExposuresRef = useRef(allManualExposures);
  allManualExposuresRef.current = allManualExposures;

  const { data: catConfig } = staticDataService.useConfigQuery("cat_exposures");
  const defaultsAppliedRef = useRef({});

  useEffect(() => {
    if (useMultiCatModelMode == null || !catModel || !catConfig) return;
    if (defaultsAppliedRef.current[catModel]) return;

    const rawExposures = useMultiCatModelMode
      ? catExposures?.manualExposures?.[catModel]
      : catExposures?.manualExposures;

    if (rawExposures !== undefined) {
      defaultsAppliedRef.current[catModel] = true;
      return;
    }

    const defaults = catConfig?.defaultManualExposures?.[catModel];
    if (!Array.isArray(defaults) || defaults.length === 0) {
      defaultsAppliedRef.current[catModel] = true;
      return;
    }

    const seeded = defaults.map((row) => ({ ...row, id: ids.getUniqueKey() }));

    dispatch(
      catExposuresStore.update({
        manualExposures: useMultiCatModelMode
          ? { ...catExposures?.manualExposures, [catModel]: seeded }
          : seeded,
      })
    );

    defaultsAppliedRef.current[catModel] = true;
  }, [catModel, catConfig, catExposures, useMultiCatModelMode, dispatch]);

  const updateExposures = useCallback(
    (exposures) =>
      dispatch(
        catExposuresStore.update({
          manualExposures: useMultiCatModelMode
            ? { ...allManualExposuresRef.current, [catModel]: exposures }
            : exposures,
        })
      ),
    [dispatch, useMultiCatModelMode, catModel]
  );

  const deleteExposure = (id) =>
    updateExposures(manualExposures.filter((e) => e.id !== id));

  const updateExposure = (id) => ({ field, value }) => {
    const exists = manualExposures.some((e) => e.id === id);

    if (!exists) {
      const isPriorOnlyRow =
        typeof id === "string" && id.startsWith(PRIOR_PREFIX);

      if (isPriorOnlyRow) {
        if (value == null) return;

        const priorLookupKey = id.slice(PRIOR_PREFIX.length);
        const [priorRegionPeril, ...descParts] = priorLookupKey.split("::");
        const priorDescription = descParts.join("::");

        const newExposure = {
          id: nextId,
          _REGION_PERIL: field === "_REGION_PERIL" ? value : priorRegionPeril,
          _DESCRIPTION: field === "_DESCRIPTION" ? value : priorDescription,
          [field]: value,
        };

        updateExposures([...manualExposures, newExposure]);
        setNextId(ids.getUniqueKey());
        return;
      }

      if (id !== nextId) return;
      updateExposures([
        ...manualExposures,
        {
          id: nextId,
          [field]: value,
        },
      ]);
      setNextId(ids.getUniqueKey());
      return;
    }

    setTimeout(
      () =>
        updateExposures(
          manualExposures.map((e) =>
            e.id === id
              ? {
                  ...e,
                  [field]: value != null ? value : undefined,
                }
              : e
          )
        ),
      0
    );
  };

  const { data: uploadConfig } = staticDataService.useConfigQuery(
    "cat_exposure_upload_template"
  );

  const handleUploadConfirm = useCallback(
    (exposures, mode) => {
      const merged =
        mode === "append" ? [...manualExposures, ...exposures] : exposures;
      updateExposures(merged);
      enqueueSnackbar(`Imported ${exposures.length} exposure(s)`, {
        variant: "success",
      });
    },
    [manualExposures, updateExposures, enqueueSnackbar]
  );

  const handleDownloadTemplate = useCallback(async () => {
    await generateTemplate(catConfig, uploadConfig, catModel);
  }, [catConfig, uploadConfig, catModel]);

  const { data: analyticsConfig } = staticDataService.useConfigQuery(
    "analytics"
  );

  const analyticsData = useSelector(analyticsStore.select);
  const pricingById = useMemo(
    () =>
      useMultiCatModelMode
        ? analyticsData?.programPricing?.values?.manualCat?.[catModel] ??
          EMPTY_OBJ
        : analyticsData?.programPricing?.values?.manualCat ?? EMPTY_OBJ,
    [analyticsData, catModel, useMultiCatModelMode]
  );

  const priorState = useSelector(stateSelectors.selectPriorState);
  const priorProgramPricing = priorState?.analytics?.programPricing;
  const priorCatExposures = priorState?.catExposures;

  const priorManualPricing = useMemo(() => {
    const engine = priorProgramPricing?.engine;
    const engineConfig = analyticsConfig?.engineConfig?.[engine] ?? {};
    const useMulti = engineConfig?.useMultiCatModelMode;

    let exposures = EMPTY_OBJ;
    if (useMulti) {
      exposures =
        priorCatExposures?.manualExposures?.[catModel] ??
        priorProgramPricing?.values?.manualCat?.[catModel] ??
        EMPTY_OBJ;
    } else if (catModel === engineConfig?.catModels?.[0]) {
      exposures =
        priorCatExposures?.manualExposures ??
        priorProgramPricing?.values?.manualCat ??
        EMPTY_OBJ;
    }

    return exposures;
  }, [analyticsConfig, priorProgramPricing, priorCatExposures, catModel]);

  const exposures = useMemo(
    () =>
      getExposures({
        manualExposures,
        pricingById,
        priorManualPricing,
      }),
    [manualExposures, pricingById, priorManualPricing]
  );
  const metrics = extractPerilMetrics(exposures);

  const { isFetching } = usePricing();

  return (
    <Component
      title={"Manual"}
      isFetching={isFetching}
      options={
        <Visible byTag={CAT_EXPOSURES_UPLOAD_ENABLED}>
          <Disabled ifReadOnly>
            <Stack direction={"row"} spacing={1}>
              <Button
                size={"small"}
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                {"Upload"}
              </Button>
              <Button
                size={"small"}
                startIcon={<DownloadIcon />}
                onClick={handleDownloadTemplate}
              >
                {"Template"}
              </Button>
            </Stack>
          </Disabled>
        </Visible>
      }
    >
      <Box
        sx={{
          margin: "0.5rem 0",
          padding: "0.875rem 0.375rem",
          background: theme.brand.gray[500],
          borderRadius: "0.625rem",
        }}
      >
        <Metrics metrics={metrics} />
      </Box>
      <Disabled ifReadOnly>
        <CatExposuresGrid
          catModel={catModel}
          exposures={[...exposures, { id: nextId }]}
          updateExposure={updateExposure}
          deleteExposure={deleteExposure}
          loading={isFetching}
          hideExpiring={Object.keys(priorManualPricing).length === 0}
        />
      </Disabled>
      <Visible byTag={CAT_EXPOSURES_UPLOAD_ENABLED}>
        <CatExposureUploadDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          onConfirm={handleUploadConfirm}
          catExposuresConfig={catConfig}
          uploadConfig={uploadConfig}
          catModel={catModel}
        />
      </Visible>
    </Component>
  );
};

export default ManualCatExposures;
